import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import clsx from 'clsx'

import { CALL_RESULT_REMIND_MS, leadIsTest, leadPipelineUi, type InspectionLeadRow } from '@/types/inspection-lead'
import { StatusBadge as OrderStatusBadge } from '@/components/orders/StatusBadge'
import { SourceBadge, TestBadge } from '@/components/inspections/LeadBadges'
import { LeadTimer } from '@/components/inspections/LeadTimer'
import { LotBadge } from '@/components/inspections/LotBadge'
import { callAttemptLabel, callResultLabel } from '@/utils/callResult'
import { dateFnsLocale } from '@/utils/dateLocale'
import { formatPhoneDisplay } from '@/utils/phone'
import { useNow } from '@/hooks/useNow'
import type { OrderStatus } from '@/types/enums'

/**
 * Карточка лида в колонке канбана. Статус не редактируется перетаскиванием —
 * клик ведёт на детальную, где статус и меняется. Колонка = статус, поэтому
 * сам бейдж статуса лида на карточке не дублируем.
 */
function capitalize(v: string): string {
  return v ? v[0].toUpperCase() + v.slice(1) : v
}

export function LeadKanbanCard({ lead }: { lead: InspectionLeadRow }) {
  const { t } = useTranslation()
  const dateStr = format(new Date(lead.created_at), 'd MMM HH:mm', { locale: dateFnsLocale() })
  const vehicle = [lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year]
    .filter(Boolean)
    .join(' ')
  const phone = lead.client_phone ? formatPhoneDisplay(lead.client_phone) : ''
  const ui = leadPipelineUi(lead.pipeline)
  const hasTimer = ui.timerStatuses.includes(lead.status)
  const showOrder =
    !!lead.order_status && (ui.orderBadgeStatuses == null || ui.orderBadgeStatuses.includes(lead.status))
  const showLot = ui.lotBadgeStatuses.includes(lead.status)
  const location =
    lead.location && !ui.cardLocationHiddenStatuses?.includes(lead.status) ? lead.location : null
  const clientLabel = [lead.client_name, phone].filter(Boolean).join(' · ')
  const now = useNow()
  // Отвеченный звонок без результата ФО дольше 30 минут: напоминание.
  const noCallResult =
    lead.status === 'in_progress' &&
    !!lead.pending_call_at &&
    now - new Date(lead.pending_call_at).getTime() > CALL_RESULT_REMIND_MS
  // Заказ на осмотр в работе, а время осмотра не назначено.
  const noInspectionTime =
    lead.status === 'order_created' &&
    !lead.order_inspection_at &&
    ['DRAFT', 'SEARCH', 'ASSIGNED'].includes(lead.order_status ?? '')

  return (
    <Link
      to={`/inspection-leads/${lead.id}`}
      className={clsx(
        'block rounded-[var(--stryzh-radius-lg)] bg-[color:var(--stryzh-color-surface)] p-[var(--stryzh-space-5)] transition hover:-translate-y-0.5',
        'stryzh-kanban-card',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex min-w-0 items-center gap-2">
          <SourceBadge source={lead.source} />
          {leadIsTest(lead) ? <TestBadge /> : null}
          {leadPipelineUi(lead.pipeline).sections.partner && lead.partner_external_id ? (
            <span
              title={t('leads.detail.sberCrm')}
              className="truncate font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-primary)]"
            >
              ID {lead.partner_external_id}
            </span>
          ) : null}
        </div>
        <span className="shrink-0 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {dateStr}
        </span>
      </div>

      <div className="mt-[var(--stryzh-space-3)] min-w-0 truncate font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-base)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
        {vehicle || t('common.noData')}
      </div>

      <div className="mt-[var(--stryzh-space-2)] min-w-0 truncate font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
        {clientLabel || t('common.noData')}
      </div>

      {noCallResult ? (
        <div className="mt-[var(--stryzh-space-2)]">
          <span className="rounded-full bg-[rgba(247,147,26,0.14)] px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]">
            {t('leads.kanban.noCallResult')}
          </span>
        </div>
      ) : null}

      {lead.status === 'in_progress' && lead.call_result && !noCallResult ? (
        // Результат последнего звонка ФО (решение Sage 05.11): коротко, без
        // группы «Дозвонились/Не дозвонились» (её даёт цвет), одной строкой;
        // к нему время перезвона, если стоит, или счётчик недозвонов. Пока
        // висит напоминание о звонке без результата, старый итог не показываем.
        <div className="mt-[var(--stryzh-space-2)] flex flex-wrap items-center gap-1">
          {(() => {
            const chip = clsx(
              'inline-block max-w-full truncate rounded-full px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)]',
              lead.call_result === 'no_answer' || lead.call_result === 'unreachable'
                ? 'bg-[rgba(247,147,26,0.14)] text-[color:var(--stryzh-color-accent-orange)]'
                : 'bg-[rgba(0,181,173,0.12)] text-[color:var(--stryzh-color-accent-carlink)]',
            )
            const extra =
              lead.call_result === 'callback' && lead.callback_at
                ? format(new Date(lead.callback_at), 'dd.MM HH:mm')
                : lead.call_result_attempt != null
                  ? callAttemptLabel(lead.call_result_attempt, t).toLowerCase()
                  : null
            return (
              <>
                <span className={chip} title={callResultLabel(lead.call_result, t)}>
                  {capitalize(t(`leads.callResult.value.${lead.call_result}`))}
                </span>
                {extra ? <span className={chip}>{extra}</span> : null}
              </>
            )
          })()}
        </div>
      ) : null}

      {noInspectionTime ? (
        <div className="mt-[var(--stryzh-space-2)]">
          <span className="rounded-full bg-[rgba(247,147,26,0.14)] px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]">
            {t('leads.kanban.noInspectionTime')}
          </span>
        </div>
      ) : null}

      {location || hasTimer ? (
        <div className="mt-[var(--stryzh-space-2)] flex items-center justify-between gap-2">
          <span className="min-w-0 truncate font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {location}
          </span>
          {hasTimer ? (
            <LeadTimer status={lead.status} statusChangedAt={lead.status_changed_at} />
          ) : null}
        </div>
      ) : null}

      {showOrder ? (
        <div className="mt-[var(--stryzh-space-3)] flex items-center gap-2">
          <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.kanban.order')}
          </span>
          <OrderStatusBadge status={lead.order_status as OrderStatus} />
        </div>
      ) : null}

      {showLot ? (
        <div className="mt-[var(--stryzh-space-3)] flex items-center gap-2">
          <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.kanban.lot')}
          </span>
          <LotBadge status={lead.lot_status} />
        </div>
      ) : null}
    </Link>
  )
}
